import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Row, Col } from 'react-bootstrap';
import { useTable, Row as ReactTableRow } from 'react-table';
import XLSX from 'xlsx';
import classNames from 'classnames';
import { cloneDeep, orderBy } from 'lodash';
import { ButtonV2 as Button } from '@praktio/common-ui';

import { QuickFilters } from '../QuickFilters/QuickFilters';
import FilterDropdown from '../FilterDropdown/FilterDropdown';
import OverlaySpinner from '../../common/OverlaySpinner/OverlaySpinner';
import { DownloadReportModal, DownloadOption } from '../../common/modals/DownloadReportModal';

import { RootState } from '../../../ducks/store';
import { getCourseReport } from '../../../ducks/courses';

import {
  filterTypeIsDate,
  filterTypeIsString,
  formatDateFilterTypeForDisplay,
  generateReportXlsxFileName,
  getTeamAncestorPath,
  getUniqueStringValues,
  hasTeamManagerRole,
  returnUniqueValuesInColumn,
} from '../../../utils/functions';

import {
  hasOntraCourse,
  isOntraCourse as isOntraCourseFn,
  formatPercentValue,
} from '../../../utils/reports';

import { BLANKS_FILTER, OneDay, QuickFilterOptions, Timezone } from '../../../utils/constants';
import { DateFilterType } from '../../../utils/enums';
import { useColumnWidths } from '../../../hooks/useColumnWidths';
import { useVirtualizedRows } from '../../../hooks/useVirtualizedRows';

import 'react-datepicker/dist/react-datepicker.css';
import './Report.scss';

enum SortOrder {
  Ascending = 'asc',
  Descending = 'desc',
}
interface AppliedFilterBadgeProps {
  fieldName: string;
  rows: ReactTableRow<any>[];
  appliedFilter: IReportsFilterValue;
  removeAppliedFilter: (fieldName: string) => void;
}

const AppliedFilterBadge = ({
  fieldName,
  rows,
  appliedFilter,
  removeAppliedFilter,
}: AppliedFilterBadgeProps) => {
  const uniqueElementsInColumn = returnUniqueValuesInColumn(fieldName, rows);
  const isDateFilter = filterTypeIsDate(appliedFilter.filterType);
  const displayName = appliedFilter.displayName;
  const activeStringFilters = appliedFilter.stringValues;
  const activeStringFiltersLength = activeStringFilters?.length || 0; // If there are no filters, the length is 0
  const activeStartDateFilter = appliedFilter.startDate;
  const activeEndDateFilter = appliedFilter.endDate;

  const noStringFilterApplied =
    activeStringFiltersLength === Object.keys(uniqueElementsInColumn || []).length;
  const noDateFilterApplied = appliedFilter.stringValues === DateFilterType.allIncludingBlanks;
  const noFilterApplied = isDateFilter ? noDateFilterApplied : noStringFilterApplied; // No filters applied

  // If no filters are applied, do not show the badge
  if (noFilterApplied) {
    return null;
  }

  if (
    activeStringFilters &&
    activeStringFilters.length > 0 &&
    activeStringFilters !== DateFilterType.range
  ) {
    let valueForBadge = '';

    if (isDateFilter) {
      valueForBadge = formatDateFilterTypeForDisplay(activeStringFilters as DateFilterType);
    } else {
      // If there is more than one filter applied for this field, show 'x of y' for the badge,
      // otherwise, show the selected value for filtering.
      valueForBadge =
        activeStringFilters && activeStringFilters?.length > 1
          ? `${activeStringFilters?.length} of ${Object.keys(uniqueElementsInColumn).length}`
          : (activeStringFilters as IReportFilterString[])[0].displayString;
    }

    if (valueForBadge === BLANKS_FILTER) {
      valueForBadge = 'Blanks';
    }

    return (
      <div className="AppliedFilterBadge">
        <div>{`${displayName}: ${valueForBadge}`}</div>
        <div
          className="AppliedFilterBadge-close d-flex"
          onClick={() => removeAppliedFilter(fieldName)}
        >
          <span className="material-icons">close</span>
        </div>
      </div>
    );
  }

  if (
    (activeStartDateFilter && activeEndDateFilter) ||
    (appliedFilter.before && activeEndDateFilter)
  ) {
    let valueForBadge = '';
    if (appliedFilter.before) {
      valueForBadge = `Before ${activeEndDateFilter.toDateString()}`;
    } else {
      valueForBadge = `${
        activeStartDateFilter && activeStartDateFilter.toDateString()
      } - ${activeEndDateFilter.toDateString()}`;
    }
    return (
      <div className="AppliedFilterBadge">
        <div>{`${displayName}: ${valueForBadge}`}</div>
        <div
          className="AppliedFilterBadge-close d-flex"
          onClick={() => removeAppliedFilter(fieldName)}
        >
          <span className="material-icons">close</span>
        </div>
      </div>
    );
  }
  return null;
};

interface IProps {
  user?: IUser;
  reportData?: IReportData;
  loading?: boolean;
  isCLE?: boolean;
  isGeneral?: boolean;
  teams?: ITeam[];
}

// Builds the Team column label: the team's breadcrumb path with the top-level
// parent excluded (e.g. "2026 Winter - Litigation"). Falls back to the plain
// leaf name whenever the ancestors can't be resolved — a top-level team, no
// teams list provided, or the team not found in the list.
const buildTeamLabel = (team: IReportTeam, teams?: ITeam[]): string => {
  const ancestorPath = teams ? getTeamAncestorPath(teams, team._id) : [];
  const breadcrumbTeams = ancestorPath.slice(1);

  if (!breadcrumbTeams.length) {
    return team.name;
  }

  return breadcrumbTeams.map((ancestor) => ancestor.name).join(' - ');
};

export const flattenReportData = (
  reportData?: IReportData,
  activeCourseNames?: string[],
  isCLE?: boolean,
  teams?: ITeam[],
) => {
  if (!isCLE && !activeCourseNames) {
    return [];
  }

  const processedData: any[] = [];
  reportData?.rows.forEach((r) => {
    const commonData = { ...r } as any;

    if (r.lastLogin) {
      commonData.lastLoginLocal = new Date(r.lastLogin).toLocaleString();
    }

    delete commonData.courseProgresses;
    delete commonData.teams;

    r.courseProgresses?.forEach((c, i) => {
      // Do not include courses that are not in the active course list
      if (!isCLE && !activeCourseNames?.includes(c.title)) {
        return;
      }

      const data = {
        ...commonData,
        id: `${commonData.email}-${i}`,
        courseId: c._id,
        course: c.title,
        completed: c.completed,
        started: c.started,
        progress: c.progress,
        progressFormatted: formatPercentValue(c.progress || 0),
        team: 'None',
      };

      const courseIsComplete = !!(c.progress && c.progress >= 100);

      if (isOntraCourseFn(c.title) && courseIsComplete) {
        data.score = c.score;
        data.scoreFormatted = formatPercentValue(c.score);
      }

      if (c.completed) {
        data.completed = new Date(c.completed).toLocaleString();
      }

      if (c.started) {
        data.started = new Date(c.started).toLocaleString();
      }

      if (r.teams?.length) {
        r.teams?.forEach((t) => {
          const courseAssignmentData = t.courses.find((tc) => tc._id === c._id);
          if (courseAssignmentData) {
            const { assigned } = courseAssignmentData;
            const dataWithTeam = { ...data, team: buildTeamLabel(t, teams), teamId: t._id };
            if (assigned) {
              dataWithTeam.assigned = new Date(assigned).toLocaleString();
            }
            processedData.push(dataWithTeam);
          }
        });
      }
      // This else should only be currently hit for the CLE report;
      // everything else should be coming in as team data for the General report
      else {
        processedData.push(data);
      }
    });
  });

  return processedData;
};

const Report: FC<IProps> = ({ user, reportData, loading, isCLE, isGeneral, teams }) => {
  const dispatch = useDispatch();
  const isTeamManager = hasTeamManagerRole(user);
  const [downloadingScores, setDownloadingScores] = useState<string[]>([]);

  const { activeCourseNames, loadDate } = useSelector((state: RootState) => ({
    activeCourseNames: state.teamsSlice.activeCourseNames,
    loadDate: new Date(state.report.loadDate),
  }));

  const data = useMemo(
    () => flattenReportData(reportData, activeCourseNames, isCLE, teams),
    [reportData, activeCourseNames, isCLE, teams],
  );

  const columns = useMemo(() => {
    // TODO: Figure out how to replace 'any[]' with a proper type in the future
    const columnsToUse: any[] = [
      { Header: 'Team', accessor: 'team', filterType: 'string' },
      { Header: 'First Name', accessor: 'firstName', filterType: 'string' },
      { Header: 'Last Name', accessor: 'lastName', filterType: 'string' },
      { Header: 'Email', accessor: 'email', filterType: 'string' },
      {
        Header: 'Last Login',
        accessor: 'lastLoginLocal',
        excelHeader: 'Last Login',
        filterType: 'date',
      },
      { Header: 'Course', accessor: 'course', filterType: 'string' },
      // This is a quirky column because filter values are treated as string, but sorting is treated as a number.
      // At some point, we should adjust filtering logic to treat this as a number as well
      {
        Header: '% Complete',
        accessor: 'progressFormatted',
        filterType: 'string',
        sortType: 'number',
        excelAccessor: 'progress',
        excelFormat: '0.00%',
        excelProcessValue: (value?: number) => (value || 0) / 100,
      },
      {
        Header: 'Date Started',
        accessor: 'started',
        excelHeader: 'Date Started',
        filterType: 'date',
      },
      {
        Header: 'Date Completed',
        accessor: 'completed',
        excelHeader: 'Date Completed',
        filterType: 'date',
      },
      { Header: 'Practice Group/Department', accessor: 'practiceGroup', filterType: 'string' },
      { Header: 'Office Location', accessor: 'firmOffice', filterType: 'string' },
      { Header: 'CLE', accessor: 'cles', filterType: 'string' },
    ];

    if (isCLE) {
      columnsToUse.shift();
    }

    if (isGeneral) {
      columnsToUse.push({
        Header: 'Assigned Date',
        accessor: 'assigned',
        filterType: 'date',
      });
    }

    // If Ontra courses are present, add a score column before % Complete
    if (hasOntraCourse(activeCourseNames)) {
      columnsToUse.splice(6, 0, {
        Header: 'Score',
        accessor: 'scoreFormatted',
        filterType: 'string',
        sortType: 'number',
        excelAccessor: 'score',
        excelFormat: '0.00%',
        excelProcessValue: (value?: number): number | undefined =>
          value ? value / 100 : undefined,
        Cell: ({ row }: any) => {
          const { userId, courseId, teamId, course = '', progress, scoreFormatted } = row.original;

          if (!isOntraCourseFn(course) || progress < 100) return null;

          const key = `${userId}:${courseId}:${teamId}`;
          const isDownloading = downloadingScores.includes(key);

          const handleDownload = async (e: React.MouseEvent) => {
            e.stopPropagation();

            setDownloadingScores((prev) => [...prev, key]);

            try {
              await dispatch(
                getCourseReport({
                  userId,
                  courseId,
                  isTeamManager,
                }),
              );
            } finally {
              setDownloadingScores((prev) => prev.filter((id) => id !== key));
            }
          };

          return (
            <span className="Report__ScoreColumn">
              <span>{scoreFormatted}</span>
              <Button
                className={classNames({ 'is-loading': isDownloading })}
                disabled={isDownloading}
                isLoading={isDownloading}
                title="Download score report"
                onClick={handleDownload}
              >
                <span className="material-icons-outlined">task</span>
              </Button>
            </span>
          );
        },
      });
    }

    return columnsToUse;
  }, [isCLE, isGeneral, activeCourseNames, isTeamManager, downloadingScores, dispatch]);

  // Have to cast as 'any' due to mismatched TS typings as discussed here:
  // https://github.com/tannerlinsley/react-table/issues/3064#issuecomment-786340487
  const table = useTable<any>({ columns, data });
  const { getTableProps, headerGroups, getTableBodyProps, rows, prepareRow } = table;

  const [downloadDisabled, setDownloadDisabled] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<IReportsFilterCategories>({});
  const [rowsToUse, setRowsToUse] = useState(rows);
  const [columnToSort, setColumnToSort] = useState('lastName');
  const [sortOrder, setSortOrder] = useState(SortOrder.Ascending);
  const [openDropdown, setOpenDropdown] = useState<string>();

  const defaultFilters = useRef<IReportsFilterCategories>({});

  // Virtualization
  const {
    containerRef: reportContainerRef,
    visibleRows,
    topPadding: reportTopPadding,
    bottomPadding: reportBottomPadding,
    handleScroll: handleReportScroll,
  } = useVirtualizedRows(rowsToUse);

  // Column-width stabilization for virtualized rows — see useColumnWidths JSDoc.
  const { widestValues } = useColumnWidths(rowsToUse, columns, {
    containerRef: reportContainerRef,
  });

  /**
   * Determines if any filters have been applied to the table.
   * @returns {boolean} - True if any filters have been applied, false otherwise.
   */
  const filtersHaveBeenApplied = useMemo(() => {
    return Object.keys(appliedFilters).some((filter) => {
      if (filterTypeIsDate(appliedFilters[filter].filterType)) {
        return appliedFilters[filter].stringValues !== DateFilterType.allIncludingBlanks;
      }

      return appliedFilters[filter].totalFilters !== appliedFilters[filter].stringValues?.length;
    });
  }, [appliedFilters]);

  /**
   * Removes a filter from the applied filters object.
   * This function resets the filters to their default state by reapplying all filters, which in this system means displaying all data with no filtering applied.
   * @param fieldName - The field name of the filter to reset
   * @returns
   */
  const removeAppliedFilter = (fieldName: string) => {
    const clonedFilters = cloneDeep(appliedFilters);

    if (!filterTypeIsString(clonedFilters[fieldName].filterType)) {
      delete clonedFilters[fieldName];
      setAppliedFilters(clonedFilters);
      return;
    }

    const uniqueValues = returnUniqueValuesInColumn(fieldName, rows);

    clonedFilters[fieldName].stringValues = getUniqueStringValues(uniqueValues);
    clonedFilters[fieldName].totalFilters = getUniqueStringValues(uniqueValues).length;

    setAppliedFilters(clonedFilters);
  };

  /**
   * Resets all filters to their default state.
   */
  const resetFilters = () => {
    const initialFilters = {} as IReportsFilterCategories;

    columns.forEach((c) => {
      if (!filterTypeIsString(c.filterType)) {
        return;
      }

      const uniqueValues = returnUniqueValuesInColumn(c.accessor, rows);

      initialFilters[c.accessor] = {
        displayName: c.Header,
        filterType: c.filterType,
        stringValues: getUniqueStringValues(uniqueValues),
        totalFilters: getUniqueStringValues(uniqueValues).length,
      };
    });

    setAppliedFilters(initialFilters);
  };

  /**
   * Builds a human-readable description of the applied filters.
   * @returns A string describing all active filters, e.g., "Team = 3 values selected, Last Login = 01/01/2023 - 01/31/2023".
   */
  const getAppliedFilterDescription = (): string => {
    const descriptions: string[] = [];

    Object.keys(appliedFilters).forEach((filterKey) => {
      const filter = appliedFilters[filterKey];
      const isDateFilter = filterTypeIsDate(filter.filterType);

      // Check if this filter is actually filtering (not showing all values)
      if (isDateFilter) {
        if (filter.stringValues === DateFilterType.allIncludingBlanks) return;

        if (filter.startDate && filter.endDate) {
          descriptions.push(
            `${
              filter.displayName
            } = ${filter.startDate.toLocaleDateString()} - ${filter.endDate.toLocaleDateString()}`,
          );
        } else if (filter.before && filter.endDate) {
          descriptions.push(
            `${filter.displayName} = Before ${filter.endDate.toLocaleDateString()}`,
          );
        } else if (filter.stringValues) {
          descriptions.push(
            `${filter.displayName} = ${formatDateFilterTypeForDisplay(
              filter.stringValues as DateFilterType,
            )}`,
          );
        }
      } else {
        // String filter - check if not all values are selected
        if (filter.totalFilters === filter.stringValues?.length) return;

        const values = filter.stringValues as IReportFilterString[];

        if (values?.length === 1) {
          descriptions.push(`${filter.displayName} = ${values[0].displayString}`);
        } else if (values?.length > 1) {
          descriptions.push(`${filter.displayName} = ${values.length} values selected`);
        }
      }
    });

    return descriptions.join(', ');
  };

  /**
   * Executes the download of the report.
   * @param option - Whether to download filtered or full data
   */
  const executeDownload = (option: DownloadOption) => {
    setDownloadDisabled(true); // Disable download button to briefly prevent multiple clicks
    setShowDownloadModal(false);

    setTimeout(() => {
      setDownloadDisabled(false);
    }, 2000);

    const isFiltered = option === DownloadOption.Filtered;
    const dataToExport = isFiltered ? rowsToUse.map((row) => row.original) : data;
    const reportName = isFiltered ? 'Praktio Detail Report (Filtered)' : 'Praktio Detail Report';

    const date = loadDate.toLocaleDateString();
    const time = loadDate.toLocaleTimeString(undefined, { timeStyle: 'short' });

    const filterDescription = isFiltered ? getAppliedFilterDescription() : '';
    const filterNote = filterDescription ? ` [FILTERED: ${filterDescription}]` : '';
    const headerText = `This report was downloaded on ${date} at ${time}, by ${user?.firstName} ${user?.lastName}. All dates and times are in their local time zone (${Timezone}).${filterNote}`;

    const wb = XLSX.utils.book_new();
    const colIndexesWithFormatting: number[] = [];
    const sheetData = [
      [headerText],
      columns.map((c, i) => {
        if (c.excelFormat) {
          colIndexesWithFormatting.push(i);
        }

        return c.excelHeader || c.Header;
      }),
    ];

    dataToExport.forEach((d) => {
      const sheetRow: string[] = [];
      columns.forEach((c) => {
        let value = d[c.excelAccessor || c.accessor];

        if (c.excelProcessValue) {
          value = c.excelProcessValue(value);
        }

        sheetRow.push(value);
      });

      sheetData.push(sheetRow);
    });

    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    for (const columnIndex of colIndexesWithFormatting) {
      const col = columns[columnIndex];
      const format = col.excelFormat;
      const range = XLSX.utils.decode_range(ws['!ref'] as string);

      for (let row = range.s.r + 2; row <= range.e.r; row += 1) {
        const cellRef = XLSX.utils.encode_cell({ r: row, c: columnIndex });

        if (ws[cellRef]) {
          ws[cellRef].z = format;
        }
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, generateReportXlsxFileName(reportName, loadDate));
  };

  /**
   * Handles the download button click.
   * Shows modal if filters are applied, otherwise downloads directly.
   */
  const handleDownloadClick = () => {
    if (filtersHaveBeenApplied) {
      setShowDownloadModal(true);
      return;
    }

    executeDownload(DownloadOption.Full);
  };

  useEffect(() => {
    // Whenever 'appliedFilters' changes, we'll need to update the data
    // that is being used to populate the table.
    const newRowsToUse = rows.filter((row) => {
      // Loop through appliedFilters and return true if there is a match.
      // Every applied filter needs to have a match.
      return Object.keys(appliedFilters).every((filter) => {
        // The filter object accessed with the index string (ex: 'team' or 'email' etc.)
        const thisFilter = appliedFilters[filter];
        // The filter type (ex: 'string' or 'date')
        const thisFilterType = thisFilter.filterType;
        // The content of the row for this column
        const valueInRow = row.values[filter];

        // Return true if at least one matching string value is found per row.
        if (filterTypeIsString(thisFilterType)) {
          return (thisFilter.stringValues as IReportFilterString[])?.some((filterValue) => {
            const comparisonValue = filterValue.comparisonValue;
            const comparisonValueLowercase = comparisonValue.toLocaleLowerCase();

            if (filterValue && comparisonValue !== '') {
              // For quick filter options, return true if the filter value is
              // NOT equal to the row value.
              if (thisFilter.notEqual) {
                return comparisonValueLowercase !== valueInRow?.toLocaleLowerCase();
                // Otherwise, for normal filters, return true if the filter value is equal to the row value.
              } else {
                // If the filter value is '(blanks)' and the value in the row is empty, return true.
                if (comparisonValueLowercase === BLANKS_FILTER && !valueInRow) {
                  return true;
                }

                // If the filter value is equal to the value in the row, return true.
                return comparisonValueLowercase === valueInRow?.toLocaleLowerCase();
              }
              // This condition added to allow the user to filter for rows that
              // represent never having logged in.
            } else if (comparisonValue === '' && !valueInRow) {
              return true;
            }
            return false;
          });
        }
        // Return true if the date in the row is between the start and end
        // date in the filter (inclusive)
        if (filterTypeIsDate(thisFilterType) && thisFilter.endDate) {
          const valueInRowForComparison = valueInRow ? new Date(valueInRow) : undefined;
          // Need to add a day to the end value otherwise the results will omit
          // any value that is not exact midnight on the requested day ( anything
          // after 12:01 am will be omitted without it)
          const endDatePlusOne = new Date(thisFilter.endDate.getTime() + OneDay);

          if (thisFilter.before) {
            // quickFilterId stores the key name (e.g. 'NotLoggedInTwoWeeks'),
            // so look up the value before comparing since we can't reference the key directly.
            if (
              QuickFilterOptions[thisFilter.quickFilterId!] ===
              QuickFilterOptions.NotLoggedInTwoWeeks
            ) {
              return (
                (valueInRowForComparison && valueInRowForComparison < endDatePlusOne) || !valueInRow
              );
            }
          } else if (thisFilter.startDate) {
            return (
              valueInRowForComparison &&
              thisFilter.startDate <= valueInRowForComparison &&
              valueInRowForComparison < endDatePlusOne
            );
          }
        }

        // If date filter is applied and no date range is specified
        if (filterTypeIsDate(thisFilterType) && thisFilter.stringValues) {
          const option = thisFilter.stringValues as any;

          switch (option) {
            case DateFilterType.allIncludingBlanks:
              return true;
            case DateFilterType.allExcludingBlanks:
              return valueInRow;
            case DateFilterType.blankOnly:
              return !valueInRow;
          }
        }

        return false;
      });
    });

    let rowsToSort;
    // If there are filters, set the data source as our new array.
    // Otherwise, set the data source as an empty array so that no data is displayed.
    if (Object.keys(appliedFilters).length === 0) {
      rowsToSort = [] as ReactTableRow<any>[];
    } else {
      rowsToSort = newRowsToUse;
    }

    const column = columns.find((c) => c.accessor === columnToSort);
    const sortedRows = orderBy(
      rowsToSort,
      (row) => {
        const value = row.original[columnToSort];
        switch (column?.sortType || column?.filterType) {
          case 'date':
            return value ? new Date(value) : value;
          case 'number':
            return parseFloat(value || '0');
          case 'string':
          default:
            return (value || '').replace(/\s/g, '').toLowerCase();
        }
      },
      [sortOrder],
    );

    setRowsToUse(sortedRows);
  }, [appliedFilters, rows, columnToSort, sortOrder, columns]);

  useEffect(() => {
    const initialFilters = {} as IReportsFilterCategories;

    columns.forEach((c) => {
      // If filter is not a string, do not apply a filter
      if (c.filterType !== 'string') return;

      const uniqueValues = returnUniqueValuesInColumn(c.accessor, rows);

      if (Object.keys(uniqueValues).length === 0) return;

      initialFilters[c.accessor] = {
        displayName: c.Header,
        filterType: c.filterType,
        stringValues: getUniqueStringValues(uniqueValues),
        totalFilters: getUniqueStringValues(uniqueValues).length,
      };
    });

    defaultFilters.current = initialFilters;

    setAppliedFilters((prev) => (Object.keys(prev).length === 0 ? initialFilters : prev));
  }, [columns, rows]);

  return (
    <>
      {!!loading && <OverlaySpinner removeBgColor />}

      {!loading && (
        <div className="Report-content">
          <Row className="ml-0 mr-0 w-100">
            <Col>
              <div className="Report-top-content">
                <div className="Report-quick-filters-wrapper">
                  <QuickFilters
                    defaultFilters={defaultFilters.current}
                    appliedFilters={appliedFilters}
                    choices={[
                      QuickFilterOptions.NotCompletedCourse,
                      QuickFilterOptions.NotLoggedIn,
                      QuickFilterOptions.NotLoggedInTwoWeeks,
                      QuickFilterOptions.CompletedCourse,
                    ]}
                    dropdownId="quick-filters-dropdown"
                    placeholderText="Quick Filters"
                    setAppliedFilters={setAppliedFilters}
                  />

                  {!!filtersHaveBeenApplied && (
                    <div className="Report-clear-filters-button" onClick={resetFilters}>
                      Clear Filters
                    </div>
                  )}
                </div>
                <Button
                  disabled={downloadDisabled}
                  isLoading={downloadDisabled}
                  variant="primary"
                  icon={{ name: 'download', position: 'left' }}
                  onClick={handleDownloadClick}
                >
                  DOWNLOAD REPORT
                </Button>
              </div>
              <div className="Report-active-filters-container">
                {Object.keys(appliedFilters).map((value) => {
                  return (
                    <AppliedFilterBadge
                      fieldName={value}
                      rows={rows}
                      appliedFilter={appliedFilters[value]}
                      removeAppliedFilter={removeAppliedFilter}
                      key={value}
                    />
                  );
                })}
              </div>
              <div
                className="Report-table-wrapper"
                ref={reportContainerRef}
                onScroll={handleReportScroll}
              >
                <Table {...getTableProps()} className="Report-table">
                  <thead>
                    {headerGroups.map((headerGroup) => {
                      return (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                          {headerGroup.headers.map((col) => {
                            return (
                              <th
                                {...col.getHeaderProps()}
                                className={classNames(
                                  'Report-table-column-header align-items-center',
                                  {
                                    'dropdown-open': openDropdown === col.id,
                                  },
                                )}
                              >
                                <div className="Report-table-header d-flex align-items-center justify-content-between">
                                  <div className="Report-table-header-left-content d-flex align-items-center">
                                    {/* Header */}
                                    <div className="Report-table-header-name">
                                      {col.render('Header')}
                                    </div>

                                    {/* Sort Button */}
                                    <div
                                      className={classNames(
                                        'Report-table-header-sort-button d-flex align-items-center',
                                        {
                                          activeSort: columnToSort === col.id,
                                        },
                                      )}
                                      onClick={() => {
                                        if (columnToSort !== col.id) {
                                          setColumnToSort(col.id);
                                          setSortOrder(SortOrder.Ascending);
                                        } else {
                                          setSortOrder(
                                            sortOrder === SortOrder.Ascending
                                              ? SortOrder.Descending
                                              : SortOrder.Ascending,
                                          );
                                        }
                                      }}
                                    >
                                      <span className=" material-symbols-sharp">
                                        {columnToSort === col.id
                                          ? sortOrder === SortOrder.Descending
                                            ? 'arrow_downward'
                                            : 'arrow_upward'
                                          : 'unfold_more'}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Filter Menu */}
                                  <FilterDropdown
                                    column={col.id}
                                    filterType={(col as any).filterType}
                                    sortType={(col as any).sortType}
                                    displayString={col.render('Header')?.toString()}
                                    rows={rows}
                                    appliedFilters={appliedFilters}
                                    setAppliedFilters={setAppliedFilters}
                                    setOpenDropdown={setOpenDropdown}
                                  />
                                </div>
                                {/* Zero-height sizer for column-width stabilization (see useColumnWidths). */}
                                {widestValues[col.id] != null && (
                                  <div
                                    aria-hidden="true"
                                    style={{
                                      height: 0,
                                      overflow: 'hidden',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {widestValues[col.id]}
                                  </div>
                                )}
                              </th>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {reportTopPadding > 0 && (
                      <tr>
                        <td
                          colSpan={columns.length}
                          style={{ height: reportTopPadding, padding: 0, border: 'none' }}
                        />
                      </tr>
                    )}
                    {visibleRows.map((row) => {
                      prepareRow(row);
                      return (
                        <tr {...row.getRowProps()}>
                          {row.cells.map((cell) => {
                            return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
                          })}
                        </tr>
                      );
                    })}
                    {reportBottomPadding > 0 && (
                      <tr>
                        <td
                          colSpan={columns.length}
                          style={{ height: reportBottomPadding, padding: 0, border: 'none' }}
                        />
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Col>
          </Row>
        </div>
      )}

      <DownloadReportModal
        show={showDownloadModal}
        isDownloading={downloadDisabled}
        onHide={() => setShowDownloadModal(false)}
        onDownload={executeDownload}
      />
    </>
  );
};

export default Report;
